import React from 'react';
import { View, Text, StyleSheet, Modal, Pressable, ScrollView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { PAYMENT_DESIGN_TOKENS, PAYMENT_TYPOGRAPHY, PAYMENT_SPACING } from '@/constants/residents/mobile/paymentDesignTokens';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { SVG_COMPONENTS } from '@/assets/admin';
import { formatCurrency } from '@/utils/common/formatting';
import { mobilePaymentsStrings } from '@/constants/residents/strings';
import { getStatusLabel, renderStatusIcon } from '@/features/payments/utils/paymentStatusUtils';
import type { HistoryRow } from '@/features/payments/hooks/useLeaseHistory';

const CloseIcon = SVG_COMPONENTS.CANCEL_X;
const InfoCircleIcon = RESIDENTS_SVG_COMPONENTS.INFORMATION_CIRCLE;

interface MobileTransactionDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  transaction: HistoryRow | null;
}

/**
 * Mobile Transaction Details Modal
 * Shows detailed information about a transaction/payment
 * Matches Figma design: "Transaction details"
 */
export const MobileTransactionDetailsModal: React.FC<MobileTransactionDetailsModalProps> = ({
  visible,
  onClose,
  transaction,
}) => {
  const insets = useSafeAreaInsets();

  if (!transaction) return null;

  // Format amount with dollars and cents
  const formatAmountWithCents = (amount: number): { dollars: string; cents: string } => {
    const formatted = formatCurrency(Math.abs(amount));
    const parts = formatted.split('.');
    return {
      dollars: parts[0],
      cents: parts[1] ? `.${parts[1]}` : '.00',
    };
  };

  const amountParts = formatAmountWithCents(transaction.amount);
  const isPending = transaction.status === 'pending';
  const isDeclined = transaction.status === 'declined';

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={onClose}
    >
      {/* Overlay */}
      <Pressable style={styles.overlay} onPress={onClose}>
        <View />
      </Pressable>

      {/* Bottom Sheet */}
      <View style={[styles.bottomSheet, { paddingBottom: Math.max(insets.bottom, 24) }]}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.headerTitle}>{mobilePaymentsStrings.transactionDetails}</Text>
          <Pressable
            style={styles.closeButton}
            onPress={onClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <CloseIcon width={16} height={16} color={PAYMENT_DESIGN_TOKENS.TEXT_MUTED} />
          </Pressable>
        </View>

        <ScrollView style={styles.scrollContent} showsVerticalScrollIndicator={false}>
          {/* Amount Card */}
          <View style={styles.amountCard}>
            <Text style={styles.amountDescription}>{transaction.description}</Text>
            <View style={styles.amountRow}>
              <Text style={styles.amountDollars}>{amountParts.dollars}</Text>
              <Text style={styles.amountCents}>{amountParts.cents}</Text>
            </View>
          </View>

          {/* Details List */}
          <View style={styles.detailsList}>
            {/* Status */}
            {transaction.status && (
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>{mobilePaymentsStrings.status}</Text>
                <View style={styles.statusRow}>
                  {renderStatusIcon(transaction.status)}
                  <Text style={styles.detailValue}>{getStatusLabel(transaction.status)}</Text>
                </View>
              </View>
            )}

            {/* Payment ID */}
            {transaction.paymentId && (
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>{mobilePaymentsStrings.paymentId}</Text>
                <Text style={styles.detailValue}>{transaction.paymentId}</Text>
              </View>
            )}

            {/* Payment Method */}
            {transaction.paymentMethod && (
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>{mobilePaymentsStrings.paymentMethod}</Text>
                <Text style={styles.detailValue}>{transaction.paymentMethod}</Text>
              </View>
            )}

            {/* Payment Amount */}
            <View style={styles.detailItem}>
              <Text style={styles.detailLabel}>{mobilePaymentsStrings.paymentAmount}</Text>
              <Text style={styles.detailValue}>{formatCurrency(Math.abs(transaction.amount))}</Text>
            </View>

            {/* Convenience Fee */}
            {transaction.convenienceFee !== undefined && transaction.convenienceFee > 0 && (
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>{mobilePaymentsStrings.convenienceFee}</Text>
                <Text style={styles.detailValue}>{formatCurrency(transaction.convenienceFee)}</Text>
              </View>
            )}

            {/* Total Charges */}
            {transaction.totalAmount !== undefined && (
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>{mobilePaymentsStrings.totalCharges}</Text>
                <Text style={styles.detailValue}>{formatCurrency(transaction.totalAmount)}</Text>
              </View>
            )}
          </View>

          {/* Info Message for Pending or Declined */}
          {(isPending || isDeclined) && (
            <>
              <View style={styles.separator} />
              <View style={styles.infoMessage}>
                {InfoCircleIcon && (
                  <View style={styles.infoIcon}>
                    <InfoCircleIcon width={48} height={48} color={PAYMENT_DESIGN_TOKENS.TEXT_MUTED} />
                  </View>
                )}
                {isPending ? (
                  <Text style={styles.infoText}>
                    {mobilePaymentsStrings.paymentsPendingMessage}
                  </Text>
                ) : (
                  <Text style={styles.infoText}>
                    {mobilePaymentsStrings.paymentsDeclinedMessage}{' '}
                    <Text style={styles.contactUsLink}>{mobilePaymentsStrings.contactUs}</Text>
                  </Text>
                )}
              </View>
            </>
          )}
        </ScrollView>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: PAYMENT_DESIGN_TOKENS.OVERLAY_BACKGROUND,
  },
  bottomSheet: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: PAYMENT_DESIGN_TOKENS.CARD_BACKGROUND_SECONDARY, // #242424
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
    maxHeight: '90%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: PAYMENT_SPACING.xxl,
    paddingTop: PAYMENT_SPACING.xl,
    paddingBottom: PAYMENT_SPACING.xl,
    borderBottomWidth: 1,
    borderBottomColor: PAYMENT_DESIGN_TOKENS.BORDER,
  },
  headerTitle: {
    ...PAYMENT_TYPOGRAPHY.headingLg,
    color: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
    flex: 1,
  },
  closeButton: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scrollContent: {
    paddingHorizontal: PAYMENT_SPACING.xxl,
  },
  amountCard: {
    backgroundColor: PAYMENT_DESIGN_TOKENS.CARD_BACKGROUND, // #2F2F2F
    borderRadius: 8,
    padding: PAYMENT_SPACING.xxl,
    alignItems: 'center',
    marginTop: PAYMENT_SPACING.xxl,
    gap: PAYMENT_SPACING.xs,
  },
  amountDescription: {
    ...PAYMENT_TYPOGRAPHY.bodySmMedium,
    color: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
  },
  amountRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  amountDollars: {
    fontSize: 24,
    fontWeight: '700',
    color: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
    lineHeight: 36,
  },
  amountCents: {
    ...PAYMENT_TYPOGRAPHY.bodySmSemiBold,
    color: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
    marginBottom: 2,
  },
  detailsList: {
    marginTop: PAYMENT_SPACING.xxl,
    gap: PAYMENT_SPACING.lg,
  },
  detailItem: {
    gap: 2,
  },
  detailLabel: {
    ...PAYMENT_TYPOGRAPHY.textXsMedium,
    color: 'rgba(238, 241, 244, 0.5)', // Figma: text-[rgba(238,241,244,0.5)]
  },
  detailValue: {
    ...PAYMENT_TYPOGRAPHY.bodySm,
    color: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  separator: {
    height: 1,
    backgroundColor: PAYMENT_DESIGN_TOKENS.BORDER,
    marginTop: PAYMENT_SPACING.xxl,
  },
  infoMessage: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: PAYMENT_SPACING.lg,
    paddingTop: PAYMENT_SPACING.xxl,
    paddingBottom: PAYMENT_SPACING.xl,
  },
  infoIcon: {
    // Icon styling
  },
  infoText: {
    flex: 1,
    ...PAYMENT_TYPOGRAPHY.bodySm,
    color: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
    lineHeight: 21,
  },
  contactUsLink: {
    color: PAYMENT_DESIGN_TOKENS.PINK_ACCENT, // #F55560 - pink/red for "contact us"
  },
});
